/**
 * Simplified redemption status groups as exposed by the API layer.
 * The API maps all raw RedemptionStatus enum values to these six groups before
 * returning search results. When filtering, "Other" is expanded client-side to
 * REDEMPTION_STATUS_OTHER_RAW_VALUES before the request is sent.
 */
export type RedemptionStatusCode =
  | "PreRefunded"
  | "PartiallyPreRefunded"
  | "EscrowedToMaturity"
  | "CrossoverRefunded"
  | "CalledByForward"
  | "Other";

export interface RedemptionStatusOption {
  code: RedemptionStatusCode;
  label: string;
}

/** Raw domain enum values collapsed into the "Other" UI group. */
export const REDEMPTION_STATUS_OTHER_RAW_VALUES = [
  "Called",
  "CalledDueToDefault",
  "SpecialRedemption",
  "MandatoryRedemption",
  "Tendered",
  "PartiallyTendered",
  "PartiallyRedeemed",
  "FinalDistribution",
  "PartialDistribution",
  "ExchangedConverted",
  "Unknown",
] as const;

export const REDEMPTION_STATUS_OPTIONS: readonly RedemptionStatusOption[] = [
  { code: "PreRefunded", label: "Pre-Refunded" },
  { code: "EscrowedToMaturity", label: "Escrowed to Maturity" },
  { code: "CrossoverRefunded", label: "Crossover Refunded" },
  { code: "PartiallyPreRefunded", label: "Partially Pre-Refunded" },
  { code: "CalledByForward", label: "Called by a Forward" },
  { code: "Other", label: "Other" },
] as const;

const CODE_SET = new Set<string>(REDEMPTION_STATUS_OPTIONS.map((o) => o.code));

export function isRedemptionStatusCode(value: string): value is RedemptionStatusCode {
  return CODE_SET.has(value);
}

const LABEL_BY_CODE = Object.fromEntries(
  REDEMPTION_STATUS_OPTIONS.map((o) => [o.code, o.label]),
) as Record<RedemptionStatusCode, string>;

export function redemptionStatusLabel(code: RedemptionStatusCode): string {
  return LABEL_BY_CODE[code];
}
